import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Flex,
  Text,
  Button,
  Icon,
  Markdown,
  Chat,
  ChatInput,
  EMessageDirection,
  EWidgetType,
  cn,
  type IChatMessage,
} from '@kognitos/lattice';
import { NonThinkingSpinner } from '@/shared/components/NonThinkingSpinner';
import { ChatActivity } from '@/shared/components/ChatActivity';
import { useIntegrationAssistant } from '../hooks/use-integration-assistant';
import {
  INTEGRATION_ASSISTANT_STARTERS,
  resolveAssistantVariant,
} from '../constants';

const STREAMING_MESSAGE_ID = '__streaming__';
const THINKING_MESSAGE_ID = '__thinking__';
const DIAGNOSIS_LOADING_MESSAGE_ID = '__diagnosis_loading__';

interface IIntegrationAssistantPanelProps {
  integrationName: string;
  integrationDisplayName: string;
  integrationVersion: string;
  organizationId: string;
  workspaceId: string;
  selectedAuthId: string | undefined;
  /**
   * Saved connection instance id. When set (Edit Connection / Configure
   * Actions flows), the agent operates on this connection.
   */
  connectionId?: string;
  /**
   * Assistant mode, read only when connectionId is set. "discover" runs the
   * discovery flow (Configure Actions); "trigger" runs the trigger setup flow
   * (Poll/Webhook Trigger dialog); otherwise the agent runs diagnosis.
   */
  mode?: 'discover' | 'trigger';
  /** Shifu's connection diagnosis, shown as the diagnosis flow's opening message. */
  diagnosis?: string;
  isDiagnosisLoading?: boolean;
  /** Blocks sending (e.g. while the connection is being re-checked). */
  disabled?: boolean;
  /**
   * Trigger id being configured (poll or webhook), passed through in "trigger"
   * mode so the agent reads the right trigger's parameters.
   */
  triggerId?: string;
  /**
   * Called when shifu autocompletes a form field via its `set_trigger_field`
   * client tool (trigger-setup mode). Lets the host dialog write the value into
   * its form. Omit in flows that have no form to fill.
   */
  onFillForm?: (field: string, value: string) => string | void;
  /**
   * Called when shifu adds a filter condition via its `add_trigger_filter`
   * client tool. Returns an error message if the field/operator is invalid, so
   * shifu can correct; nothing on success.
   */
  onAddFilter?: (
    field: string,
    operator: string,
    value: string,
  ) => string | void;
  /** Called when shifu removes a filter via `remove_trigger_filter`. */
  onRemoveFilter?: (
    field: string,
    operator: string,
    value: string,
  ) => string | void;
  /** Called when shifu clears all filters via `clear_trigger_filters`. */
  onClearFilters?: () => void;
  isOpen: boolean;
  /**
   * Collapses the panel. Only wired when `closable` is true; omit it in flows
   * that pin the assistant open (e.g. trigger setup).
   */
  onClose?: () => void;
  /**
   * Which side of the dialog the panel docks to. Trigger-setup uses `left`
   * (the assistant is the entry point); the other flows keep the default
   * `right` side-panel.
   */
  side?: 'left' | 'right';
  /**
   * Whether to show the collapse button. Trigger-setup passes `false` so the
   * assistant can't be dismissed (it drives the whole flow).
   */
  closable?: boolean;
}

/**
 * Side panel with an AI-powered chat assistant for helping users
 * set up integration connections. Shown alongside the AddConnectionDialog form.
 */
export function IntegrationAssistantPanel({
  integrationName,
  integrationDisplayName,
  integrationVersion,
  organizationId,
  workspaceId,
  selectedAuthId,
  connectionId,
  mode,
  diagnosis,
  isDiagnosisLoading = false,
  disabled = false,
  triggerId,
  onFillForm,
  onAddFilter,
  onRemoveFilter,
  onClearFilters,
  isOpen,
  onClose,
  side = 'right',
  closable = true,
}: IIntegrationAssistantPanelProps) {
  const { t } = useTranslation('integrations');
  const [inputValue, setInputValue] = useState('');

  // Same flow selection as the hook's greeting: discover / diagnosis / connect.
  // Drives the starter chips and the header description so an empty
  // conversation offers questions relevant to what the agent will do.
  const variant = resolveAssistantVariant(connectionId, mode);
  const starters = INTEGRATION_ASSISTANT_STARTERS[variant];
  const descriptionKey =
    variant === 'discover'
      ? 'integrations.assistant.panel.discoveryDescription'
      : variant === 'diagnosis'
        ? 'integrations.assistant.panel.diagnosisDescription'
        : variant === 'trigger'
          ? 'integrations.assistant.panel.triggerDescription'
          : 'integrations.assistant.panel.description';
  const {
    messages,
    sendMessage,
    isLoading: isGenerating,
    streamingContent,
    quickActions,
  } = useIntegrationAssistant(
    integrationName,
    integrationDisplayName,
    integrationVersion,
    organizationId,
    workspaceId,
    selectedAuthId,
    isOpen,
    connectionId,
    mode,
    diagnosis,
    { triggerId, onFillForm, onAddFilter, onRemoveFilter, onClearFilters },
  );

  const hasUserMessage = useMemo(
    () => messages.some(m => m.role === 'user'),
    [messages],
  );
  const isPreparingDiagnosis =
    variant === 'diagnosis' && isDiagnosisLoading && !hasUserMessage;
  const isLoading = isGenerating || isPreparingDiagnosis;
  const isBlocked = isLoading || disabled;

  const chatMessages = useMemo<IChatMessage[]>(() => {
    if (isPreparingDiagnosis) {
      return [
        {
          id: DIAGNOSIS_LOADING_MESSAGE_ID,
          direction: EMessageDirection.INCOMING,
          widget: {
            type: EWidgetType.CUSTOM,
            customContent: (
              <ChatActivity
                variant="thinking"
                title={t('integrations.connectionDiagnostics.analyzing')}
                isActive
                data-testid="integration-assistant-diagnosis-loading"
              />
            ),
          },
        },
      ];
    }
    const mapped: IChatMessage[] = messages.map(message => ({
      id: message.id,
      content: message.content,
      direction:
        message.role === 'user'
          ? EMessageDirection.OUTGOING
          : EMessageDirection.INCOMING,
    }));
    if (streamingContent) {
      mapped.push({
        id: STREAMING_MESSAGE_ID,
        content: streamingContent,
        direction: EMessageDirection.INCOMING,
      });
    }
    // A widget rather than a THINKING message: THINKING is lattice's grouping
    // key, which would fold the row into a collapsed box of its own.
    if (isLoading && !streamingContent) {
      mapped.push({
        id: THINKING_MESSAGE_ID,
        direction: EMessageDirection.INCOMING,
        widget: {
          type: EWidgetType.CUSTOM,
          customContent: <NonThinkingSpinner />,
        },
      });
    }
    return mapped;
  }, [messages, streamingContent, isLoading, isPreparingDiagnosis, t]);

  const handleSend = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed || isBlocked) return;
      sendMessage(trimmed);
      setInputValue('');
    },
    [isBlocked, sendMessage],
  );

  const handleQuickAction = useCallback(
    (action: string) => {
      if (isBlocked) return;
      sendMessage(action);
    },
    [isBlocked, sendMessage],
  );

  // Trigger setup leads with a proactive, agent-generated opener that always
  // ends with its own contextual follow-up suggestions, so the generic static
  // starters would only flash for the moment between the opener's completion and
  // those follow-ups landing. Suppress them there; other flows (no proactive
  // opener) still use the static starters as their entry point.
  const showStarters =
    variant !== 'trigger' &&
    !hasUserMessage &&
    !isLoading &&
    quickActions.length === 0;

  const renderMessage = useCallback((message: IChatMessage) => {
    if (message.widget?.customContent) {
      return message.widget.customContent;
    }
    if (message.direction === EMessageDirection.OUTGOING) {
      return (
        <Flex key={message.id} justify="end" className="w-full">
          <div className="bg-muted rounded-lg px-3 py-1.5 [@media(min-height:721px)]:py-2 max-w-[85%]">
            <Text className="text-sm [@media(min-height:721px)]:text-base">
              {message.content}
            </Text>
          </div>
        </Flex>
      );
    }
    return (
      <Markdown
        key={message.id}
        textProps={{ color: 'muted' }}
        textClassName="text-sm [@media(min-height:721px)]:text-base"
        size="sm"
      >
        {message.content ?? ''}
      </Markdown>
    );
  }, []);

  return (
    // The panel's width is --trigger-assistant-panel-width; the form's offset in
    // TriggerAssistantDialogLayout reads the same var, so the two stay in sync.
    <div
      inert={!isOpen}
      className={cn(
        'absolute top-3 bottom-0 transition-[width,padding] duration-300 ease-in-out',
        side === 'left' ? 'left-0' : 'right-0',
        isOpen
          ? side === 'left'
            ? 'w-[var(--trigger-assistant-panel-width)] pl-6'
            : 'w-[var(--trigger-assistant-panel-width)] pr-6'
          : side === 'left'
            ? 'w-0 pl-0'
            : 'w-0 pr-0',
      )}
    >
      <Card
        className="w-[calc(var(--trigger-assistant-panel-width)_-_24px)] h-full overflow-hidden shadow-sm rounded-b pt-4 ring-1 ring-border/60"
        data-testid="integration-assistant-panel"
      >
        <Flex
          direction="column"
          gap={3}
          className="h-full w-full min-h-0 overflow-hidden"
        >
          {/* Card Header */}
          <Flex direction="column" gap={1.5} className="w-full px-6">
            <Flex align="center" justify="between" className="w-full">
              <Flex align="center" gap={2}>
                <Icon type="Sparkle" size="sm" className="text-primary" />
                <Text weight="semibold">
                  {t('integrations.assistant.panel.title')}
                </Text>
              </Flex>
              {closable && (
                <Button
                  variant="outline"
                  size="icon-sm"
                  className="-mr-2 cursor-pointer"
                  onClick={onClose}
                  aria-label={t(
                    'integrations.assistant.panel.collapseAriaLabel',
                  )}
                  data-testid="integration-assistant-close-button"
                >
                  <Icon type="Minus" size="sm" />
                </Button>
              )}
            </Flex>
            <Text
              color="muted"
              level="small"
              className="[@media(max-height:720px)]:hidden"
            >
              {t(descriptionKey)}
            </Text>
          </Flex>

          <Chat
            messages={chatMessages}
            renderMessage={renderMessage}
            showSender={false}
            showTimestamp={false}
            showGradient={false}
            showEmptyState={false}
            // ChatActivity's `compact` padding for the thinking row, which
            // NonThinkingSpinner takes no props to ask for itself.
            chatContainerClassName="[&_[data-slot=chat-activity]>*:first-child]:py-0.5"
            className="flex-1 min-h-0 px-6"
          >
            {showStarters && (
              <Flex direction="column" gap={2} className="w-full pt-2">
                {starters.map(starter => (
                  <Button
                    key={starter.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickAction(t(starter.promptKey))}
                    disabled={isBlocked}
                    className="w-fit max-w-full h-auto py-2 whitespace-normal text-left"
                  >
                    {t(starter.labelKey)}
                  </Button>
                ))}
              </Flex>
            )}
            {quickActions.length > 0 && (
              <Flex direction="column" gap={2} className="w-full pt-2">
                {quickActions.map(action => (
                  <Button
                    key={action}
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickAction(action)}
                    disabled={isBlocked}
                    className="w-fit max-w-full h-auto py-2 whitespace-normal text-left"
                  >
                    {action}
                  </Button>
                ))}
              </Flex>
            )}
          </Chat>

          <Flex direction="column" gap={3} className="shrink-0 w-full px-6">
            <ChatInput
              variant="inline"
              className="[&_[data-slot=chat-input-icon]]:hidden [&_[data-slot=chat-input-send-button]:disabled]:opacity-100"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onSend={handleSend}
              isLoading={isLoading}
              disabled={disabled}
              loadingMode="loader"
              placeholder={t('integrations.assistant.panel.inputPlaceholder')}
              showAttachmentButton={false}
              data-testid="integration-assistant-input"
            />
          </Flex>
        </Flex>
      </Card>
    </div>
  );
}
